import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import ListGroup from 'react-bootstrap/ListGroup';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import './ManagerDashboard.css';
import VoiceRecorder from '../components/VoiceRecorder';

import { useSocket } from '../SocketContext';

export default function ManagerDashboard() {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const { socket } = useSocket();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('newest');
    const [filterCategory, setFilterCategory] = useState('All');
    const [activeTab, setActiveTab] = useState('all');
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [replyVoiceNote, setReplyVoiceNote] = useState(null);
    const [replyVoiceUrl, setReplyVoiceUrl] = useState('');
    const [replyImage, setReplyImage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [showRecorder, setShowRecorder] = useState(false);
    const [isInitiatingMessage, setIsInitiatingMessage] = useState(null); // orderId
    const [initMessageText, setInitMessageText] = useState('');
    const [initVoiceNote, setInitVoiceNote] = useState(null);
    const [initVoiceUrl, setInitVoiceUrl] = useState('');
    const [initImage, setInitImage] = useState('');



    useEffect(() => {
        const fetchOrders = async () => {
             // ... (existing fetch logic)
            try {
                setLoading(true);
                let statusQuery = activeTab;
                if (activeTab === 'cooking') statusQuery = 'pending,preparing,ready';
                if (activeTab === 'delivery') statusQuery = 'ready,served';
                if (activeTab === 'history') statusQuery = 'completed,cancelled';
                if (activeTab === 'all') statusQuery = 'pending,preparing,ready,served';

                const { data } = await axios.get(
                    `/api/orders/manager/dashboard?status=${statusQuery}&category=${filterCategory === 'All' ? '' : filterCategory}&sortBy=${sortBy}`,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                setOrders(data);
                setLoading(false);
            } catch (err) {
                toast.error(getError(err));
                setLoading(false);
            }
        };
        fetchOrders();

        if (socket) {
             socket.on('new_order', (newOrder) => {
                 toast.info(`New Order from Table ${newOrder.tableNumber}`);
                 setOrders((prevOrders) => [newOrder, ...prevOrders]);
             });

             socket.on('order_updated', (updatedOrder) => {
                 setOrders((prevOrders) => 
                     prevOrders.map((o) => o._id === updatedOrder._id ? updatedOrder : o)
                        .filter(o => {
                             // Apply simple filtering logic here or re-fetch if complex
                             // For now, keep it simple or maybe just trigger fetchOrders();
                             // But let's try to update locally to avoid fetch.
                             const cookingStatuses = ['pending', 'preparing', 'ready'];
                             const deliveryStatuses = ['ready', 'served'];
                            
                             let shouldKeep = false;
                             if (activeTab === 'all') shouldKeep = (updatedOrder.orderStatus !== 'completed' && updatedOrder.orderStatus !== 'cancelled');
                             else if (activeTab === 'cooking' && cookingStatuses.includes(updatedOrder.orderStatus)) shouldKeep = true;
                             else if (activeTab === 'delivery' && deliveryStatuses.includes(updatedOrder.orderStatus)) shouldKeep = true;
                             else if (activeTab === 'history' && (updatedOrder.orderStatus === 'completed' || updatedOrder.orderStatus === 'cancelled')) shouldKeep = true;
                             else if (activeTab === updatedOrder.orderStatus) shouldKeep = true;

                             return shouldKeep;
                        })
                 );
             });

             socket.on('new_feedback', ({ orderId, feedback }) => {
                 toast.warning(`New Feedback on an order!`);
                 setOrders((prevOrders) => 
                     prevOrders.map((o) => {
                         if (o._id === orderId) {
                             return { ...o, feedbacks: [...o.feedbacks, feedback] };
                         }
                         return o;
                     })
                 );
             });
        }

        return () => {
            if (socket) {
                socket.off('new_order');
                socket.off('order_updated');
                socket.off('new_feedback');
            }
        };
    }, [userInfo, activeTab, filterCategory, sortBy, replyingTo, socket]);

    const updateStatusHandler = async (orderId, newStatus) => {
        try {
            await axios.put(
                `/api/orders/${orderId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );

            // Update local state
            setOrders(orders.map(order =>
                order._id === orderId ? { ...order, orderStatus: newStatus } : order
            ));

            toast.success(`Order status updated to ${newStatus}`);

            // Refresh logic based on new status categories
            const cookingStatuses = ['pending', 'preparing', 'ready'];
            const deliveryStatuses = ['ready', 'served'];

            let shouldKeep = false;
            if (activeTab === 'all') shouldKeep = (newStatus !== 'completed' && newStatus !== 'cancelled');
            else if (activeTab === 'cooking' && cookingStatuses.includes(newStatus)) shouldKeep = true;
            else if (activeTab === 'delivery' && deliveryStatuses.includes(newStatus)) shouldKeep = true;
            else if (activeTab === 'history' && (newStatus === 'completed' || newStatus === 'cancelled')) shouldKeep = true;
            else if (activeTab === newStatus) shouldKeep = true;

            if (!shouldKeep) {
                setOrders(orders.filter(order => order._id !== orderId));
            }
        } catch (err) {
            toast.error(getError(err));
        }
    };

    const uploadFileHandler = async (file) => {
        if (!file) return null;
        try {
            setIsUploading(true);
            const bodyFormData = new FormData();
            
            // For audio blobs, we need to explicitly provide a filename
            if (file instanceof Blob && !(file instanceof File)) {
                const extension = file.type.includes('webm') ? 'webm' : 
                                 file.type.includes('ogg') ? 'ogg' : 
                                 file.type.includes('wav') ? 'wav' : 'bin';
                bodyFormData.append('file', file, `audio_record_${Date.now()}.${extension}`);
            } else {
                bodyFormData.append('file', file);
            }

            const { data } = await axios.post('/api/upload', bodyFormData, {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`,
                },
            });
            setIsUploading(false);
            return data.secure_url;
        } catch (err) {
            console.error("Upload error:", err);
            toast.error(getError(err));
            setIsUploading(false);
            return null;
        }
    };

    const submitReplyHandler = async (orderId, feedbackId) => {
        if (!replyText && !replyVoiceNote && !replyImage) return;
        try {
            let voiceUrl = '';
            if (replyVoiceNote) {
                voiceUrl = await uploadFileHandler(replyVoiceNote);
            }

            const { data } = await axios.post(
                `/api/orders/${orderId}/feedback/${feedbackId}/reply`,
                { 
                    reply: replyText,
                    replyVoiceNote: voiceUrl,
                    replyImage: replyImage
                },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );
            toast.success('Reply sent');

            // Update local state with the updated order from response
            setOrders(orders.map(order => order._id === orderId ? data.order : order));

            setReplyingTo(null);
            setReplyText('');
            setReplyVoiceNote(null);
            setReplyVoiceUrl('');
            setReplyImage('');
            setShowRecorder(false);
        } catch (err) {
            toast.error(getError(err));
        }
    };

    const submitInitiatedMessageHandler = async (orderId) => {
        if (!initMessageText && !initVoiceNote && !initImage) return;
        try {
            let voiceUrl = '';
            if (initVoiceNote) {
                voiceUrl = await uploadFileHandler(initVoiceNote);
            }

            const { data } = await axios.post(
                `/api/orders/${orderId}/manager-message`,
                { 
                    message: initMessageText,
                    voiceNote: voiceUrl,
                    image: initImage
                },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );
            toast.success('Message sent');

            // Update local state with the updated order from response
            setOrders(orders.map(order => order._id === orderId ? data.order : order));

            setIsInitiatingMessage(null);
            setInitMessageText('');
            setInitVoiceNote(null);
            setInitVoiceUrl('');
            setInitImage('');
            setShowRecorder(false);
        } catch (err) {
            toast.error(getError(err));
        }
    };

    const updateItemStatusHandler = async (orderId, itemId, newStatus) => {
        try {
            await axios.put(
                `/api/orders/${orderId}/item/${itemId}/status`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );

            // Update local state
            setOrders(orders.map(order => {
                if (order._id === orderId) {
                    const updatedItems = order.orderItems.map(item =>
                        item._id === itemId ? { ...item, status: newStatus } : item
                    );
                    return { ...order, orderItems: updatedItems };
                }
                return order;
            }));

            toast.success(`Item status updated to ${newStatus}`);
        } catch (err) {
            toast.error(getError(err));
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending': return 'danger';
            case 'confirmed': return 'warning';
            case 'preparing': return 'info';
            case 'ready': return 'primary';
            case 'served': return 'success';
            default: return 'secondary';
        }
    };

    const getDownloadUrl = (url) => {
        if (!url) return '';
        // Firebase Storage URLs don't need the Cloudinary fl_attachment transformation.
        return url;
    };

    return (
        <div className="manager-dashboard">
            <Helmet>
                <title>Kitchen Display System</title>
            </Helmet>

            <div className="dashboard-header bg-dark text-white p-3 mb-4 sticky-top">
                <Container fluid>
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <h3 className="m-0">
                            <i className="fas fa-concierge-bell me-2"></i>
                            Kitchen Display System
                        </h3>

                        <div className="d-flex gap-3 align-items-center mt-2 mt-md-0">
                            <Form.Select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                size="sm"
                                style={{ width: '150px' }}
                            >
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="table">By Table #</option>
                            </Form.Select>

                            <Form.Select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                size="sm"
                                style={{ width: '150px' }}
                            >
                                <option value="All">All Items</option>
                                <option value="Drinks">Drinks</option>
                                <option value="Food">Food</option>
                                <option value="Cocktails">Cocktails</option>
                            </Form.Select>

                            <Button variant="success" size="sm" className="me-2" onClick={() => window.location.href = '/admin/waiter-order'}>
                                <i className="fas fa-plus-circle"></i> Place Order for Customer
                            </Button>
                            <Button variant="light" size="sm" className="me-2" onClick={() => window.location.href = '/admin/workers'}>
                                <i className="fas fa-users-cog"></i> Manage Waiters
                            </Button>
                            <Button variant="light" size="sm" onClick={() => window.location.reload()}>
                                <i className="fas fa-sync-alt"></i>
                            </Button>
                        </div>
                    </div>
                </Container>
            </div>

            <Container fluid>
                <Tabs
                    id="order-status-tabs"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4 custom-tabs"
                >
                    <Tab eventKey="all" title="KDS Overview" />
                    <Tab eventKey="cooking" title="Kitchen (Preparing)" />
                    <Tab eventKey="delivery" title="Service (Delivery)" />
                    <Tab eventKey="history" title="Recently Closed" />
                </Tabs>

                {loading ? (
                    <div className="text-center mt-5">Loading orders...</div>
                ) : orders.length === 0 ? (
                    <div className="text-center mt-5 p-5 bg-light rounded text-muted">
                        <h4>No orders found</h4>
                        <p>Waiting for new orders...</p>
                    </div>
                ) : (
                    <Row>
                        {orders.map((order) => (
                            <Col key={order._id} xs={12} md={6} lg={4} xl={3} className="mb-4">
                                <Card className={`h-100 shadow-sm border-0 order-card status-${order.orderStatus}`}>
                                    <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom-0 pt-3">
                                        <h5 className="m-0 fw-bold">
                                            <span className="badge bg-dark rounded-pill me-2">
                                                {order.locationType === 'room' ? 'Room ' : 'Table '}
                                                {order.tableNumber}
                                            </span>
                                            {order.tableId?.location && <small className="text-muted fw-normal fst-italic ms-1" style={{ fontSize: '0.7em' }}>({order.tableId.location})</small>}
                                        </h5>
                                        <span className="small text-muted">
                                            {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-2">
                                            <Badge bg={getStatusBadge(order.orderStatus)} className="mb-2">
                                                {order.orderStatus.toUpperCase()}
                                            </Badge>
                                            {order.customerName !== 'Guest' && (
                                                <div className="small text-muted mb-2">Guest: {order.customerName}</div>
                                            )}
                                        </div>

                                        <ListGroup variant="flush" className="mb-3">
                                            {order.orderItems.map((item) => (
                                                <ListGroup.Item key={item._id} className="px-0 py-2 border-bottom">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex align-items-center">
                                                                <span className="fw-bold me-2">{item.quantity}x</span>
                                                                <span>{item.name}</span>
                                                                <Badge bg={getStatusBadge(item.status || 'pending')} className="ms-2" style={{ fontSize: '0.65rem' }}>
                                                                    {(item.status || 'pending').toUpperCase()}
                                                                </Badge>
                                                            </div>
                                                            {item.specialInstructions && (
                                                                <div className="text-danger small fst-italic mt-1 ms-4">
                                                                    <i className="fas fa-exclamation-circle me-1"></i>
                                                                    {item.specialInstructions}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="item-controls d-flex gap-1 flex-wrap justify-content-end" style={{ maxWidth: '180px' }}>
                                                            {(item.status || 'pending') === 'pending' && (
                                                                <Button size="sm" variant="outline-primary" className="py-0 px-1" style={{ fontSize: '0.65rem' }} onClick={() => updateItemStatusHandler(order._id, item._id, 'preparing')}>
                                                                    Prepare
                                                                </Button>
                                                            )}
                                                            {(item.status || 'pending') === 'preparing' && (
                                                                <Button size="sm" variant="outline-success" className="py-0 px-1" style={{ fontSize: '0.65rem' }} onClick={() => updateItemStatusHandler(order._id, item._id, 'ready')}>
                                                                    Ready
                                                                </Button>
                                                            )}
                                                            {(item.status || 'pending') === 'ready' && (
                                                                <Button size="sm" variant="outline-secondary" className="py-0 px-1" style={{ fontSize: '0.65rem' }} onClick={() => updateItemStatusHandler(order._id, item._id, 'served')}>
                                                                    Serve
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>

                                        {(order.specialInstructions || order.specialInstructionsAudio) && (
                                            <div className="alert alert-info py-2 mb-3 small">
                                                <strong><i className="fas fa-info-circle me-2"></i>Special Instructions:</strong>
                                                {order.specialInstructions && (
                                                    <div className="mt-1">{order.specialInstructions}</div>
                                                )}
                                                {order.specialInstructionsAudio && (
                                                    <div className="mt-2">
                                                        <audio src={order.specialInstructionsAudio} controls style={{ width: '100%', height: '40px' }} />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {order.feedbacks && order.feedbacks.length > 0 && (
                                            <div className="feedbacks-section mt-3">
                                                <h6 className="small fw-bold text-danger border-bottom pb-1">
                                                    <i className="fas fa-comment-dots me-2"></i>
                                                    Customer Claims
                                                </h6>
                                                {order.feedbacks.map((f, i) => (
                                                    <div key={i} className="mb-2">
                                                        <div className="small text-danger bg-light-danger p-2 rounded mb-1 border-start border-danger border-3">
                                                            {f.comment && <div>{f.comment}</div>}
                                                            {f.image && (
                                                                <div className="position-relative d-inline-block mt-1">
                                                                    <img src={f.image} alt="Customer" className="img-thumbnail" style={{ maxHeight: '100px' }} />
                                                                    <a href={getDownloadUrl(f.image)} download className="btn btn-sm btn-light position-absolute bottom-0 end-0 m-1 rounded-circle shadow-sm" title="Download">
                                                                        <i className="fas fa-download text-dark"></i>
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {f.voiceNote && (
                                                                <div className="mt-1 d-flex align-items-center gap-2">
                                                                    <audio src={f.voiceNote} controls style={{ width: '100%', height: '45px' }} />
                                                                    <a href={getDownloadUrl(f.voiceNote)} download className="btn btn-sm btn-outline-secondary rounded-circle" title="Download">
                                                                        <i className="fas fa-download"></i>
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {f.reply || f.replyVoiceNote || f.replyImage ? (
                                                            <div className="small text-success ms-3 bg-light p-2 rounded border-start border-success border-3">
                                                                {f.reply && <div><strong>Reply: </strong> {f.reply}</div>}
                                                                {f.replyImage && (
                                                                    <div className="position-relative d-inline-block mt-1">
                                                                        <img src={f.replyImage} alt="Manager" className="img-thumbnail" style={{ maxHeight: '100px' }} />
                                                                        <a href={getDownloadUrl(f.replyImage)} download className="btn btn-sm btn-light position-absolute bottom-0 end-0 m-1 rounded-circle shadow-sm" title="Download">
                                                                            <i className="fas fa-download text-dark"></i>
                                                                        </a>
                                                                    </div>
                                                                )}
                                                                {f.replyVoiceNote && (
                                                                    <div className="mt-1 d-flex align-items-center gap-2">
                                                                        <audio src={f.replyVoiceNote} controls style={{ width: '100%', height: '45px' }} />
                                                                        <a href={getDownloadUrl(f.replyVoiceNote)} download className="btn btn-sm btn-outline-secondary rounded-circle" title="Download">
                                                                            <i className="fas fa-download"></i>
                                                                        </a>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : replyingTo === f._id ? (
                                                            <div className="ms-3 mt-1 p-2 bg-light rounded border">
                                                                {replyImage && (
                                                                    <div className="position-relative mb-2" style={{ maxWidth: '100px' }}>
                                                                        <img src={replyImage} alt="To upload" className="img-thumbnail" />
                                                                        <Button variant="danger" size="sm" className="position-absolute top-0 end-0 p-0" style={{ width: '20px', height: '20px' }} onClick={() => setReplyImage('')}>&times;</Button>
                                                                    </div>
                                                                )}
                                                                <Form.Control
                                                                    size="sm"
                                                                    as="textarea"
                                                                    rows={2}
                                                                    placeholder="Type reply..."
                                                                    value={replyText}
                                                                    onChange={(e) => setReplyText(e.target.value)}
                                                                    className="mb-1"
                                                                />
                                                                <div className="d-flex gap-2 mb-2">
                                                                    <label className="btn btn-outline-secondary btn-sm m-0">
                                                                        <i className="fas fa-camera"></i>
                                                                        <input type="file" hidden accept="image/*" onChange={async (e) => {
                                                                            const url = await uploadFileHandler(e.target.files[0]);
                                                                            if (url) setReplyImage(url);
                                                                        }} />
                                                                    </label>
                                                                    <Button size="sm" variant="outline-secondary" onClick={() => setShowRecorder(true)}>
                                                                        <i className="fas fa-microphone"></i>
                                                                    </Button>
                                                                </div>
                                                                
                                                                {showRecorder && (
                                                                    <VoiceRecorder 
                                                                        onRecordingComplete={(blob, url) => {
                                                                            setReplyVoiceNote(blob);
                                                                            setReplyVoiceUrl(url);
                                                                            // Don't close recorder - let user see the preview
                                                                        }}
                                                                        onCancel={() => {
                                                                            setShowRecorder(false);
                                                                            setReplyVoiceNote(null);
                                                                            setReplyVoiceUrl('');
                                                                        }}
                                                                    />
                                                                )}

                                                                {replyVoiceUrl && (
                                                                    <div className="mt-2 p-2 bg-light rounded border">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <audio src={replyVoiceUrl} controls style={{ width: '100%', height: '40px' }} />
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline-danger" 
                                                                                onClick={() => {
                                                                                    setReplyVoiceNote(null);
                                                                                    setReplyVoiceUrl('');
                                                                                    setShowRecorder(false);
                                                                                }}
                                                                                title="Delete recording"
                                                                            >
                                                                                <i className="fas fa-trash"></i>
                                                                            </Button>
                                                                            <Button 
                                                                                size="sm" 
                                                                                variant="outline-success" 
                                                                                onClick={() => setShowRecorder(false)}
                                                                                title="Keep recording"
                                                                            >
                                                                                <i className="fas fa-check"></i>
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                <div className="d-flex gap-2 mt-2">
                                                                    <Button size="sm" variant="success" disabled={isUploading} onClick={() => submitReplyHandler(order._id, f._id)}>
                                                                        {isUploading ? 'Uploading...' : 'Send'}
                                                                    </Button>
                                                                    <Button size="sm" variant="light" onClick={() => {
                                                                        setReplyingTo(null);
                                                                        setReplyText('');
                                                                        setReplyVoiceNote(null);
                                                                        setReplyVoiceUrl('');
                                                                        setReplyImage('');
                                                                        setShowRecorder(false);
                                                                    }}>
                                                                        Cancel
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="text-primary p-0 ms-3 text-decoration-none"
                                                                onClick={() => setReplyingTo(f._id)}
                                                            >
                                                                <i className="fas fa-reply me-1"></i> Reply to Claim
                                                            </Button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Card.Body>
                                    <Card.Footer className="bg-white border-top-0 pb-3">
                                        <div className="d-grid gap-2">
                                            {order.orderStatus === 'pending' && (
                                                <>
                                                    <Button variant="primary" onClick={() => updateStatusHandler(order._id, 'preparing')}>
                                                        Start Preparing
                                                    </Button>
                                                    <Button variant="outline-danger" size="sm" onClick={() => updateStatusHandler(order._id, 'cancelled')}>
                                                        Cancel
                                                    </Button>
                                                </>
                                            )}

                                            {order.orderStatus === 'preparing' && (
                                                <Button variant="success" onClick={() => updateStatusHandler(order._id, 'ready')}>
                                                    Mark Ready
                                                </Button>
                                            )}

                                            {order.orderStatus === 'ready' && (
                                                <Button variant="secondary" onClick={() => updateStatusHandler(order._id, 'served')}>
                                                    Mark Served
                                                </Button>
                                            )}

                                            {order.orderStatus === 'served' && (
                                                <Button variant="outline-success" onClick={() => updateStatusHandler(order._id, 'completed')}>
                                                    Finish & Close
                                                </Button>
                                            )}
                                            
                                            <Button variant="outline-primary" size="sm" onClick={() => setIsInitiatingMessage(order._id)}>
                                                <i className="fas fa-comment-medical me-1"></i> Message Customer
                                            </Button>

                                            {isInitiatingMessage === order._id && (
                                                <div className="mt-3 p-3 bg-light rounded border border-primary">
                                                    <h6>Initiate Conversation</h6>
                                                    {initImage && (
                                                        <div className="position-relative mb-2" style={{ maxWidth: '100px' }}>
                                                            <img src={initImage} alt="To upload" className="img-thumbnail" />
                                                            <Button variant="danger" size="sm" className="position-absolute top-0 end-0 p-0" style={{ width: '20px', height: '20px' }} onClick={() => setInitImage('')}>&times;</Button>
                                                        </div>
                                                    )}
                                                    <Form.Control
                                                        size="sm"
                                                        as="textarea"
                                                        rows={2}
                                                        placeholder="Type message to customer..."
                                                        value={initMessageText}
                                                        onChange={(e) => setInitMessageText(e.target.value)}
                                                        className="mb-2"
                                                    />
                                                    <div className="d-flex gap-2 mb-2">
                                                        <label className="btn btn-outline-secondary btn-sm m-0">
                                                            <i className="fas fa-camera"></i>
                                                            <input type="file" hidden accept="image/*" onChange={async (e) => {
                                                                const url = await uploadFileHandler(e.target.files[0]);
                                                                if (url) setInitImage(url);
                                                            }} />
                                                        </label>
                                                        <Button size="sm" variant="outline-secondary" onClick={() => setShowRecorder(true)}>
                                                            <i className="fas fa-microphone"></i>
                                                        </Button>
                                                    </div>

                                                    {showRecorder && (
                                                        <VoiceRecorder 
                                                            onRecordingComplete={(blob, url) => {
                                                                setInitVoiceNote(blob);
                                                                setInitVoiceUrl(url);
                                                            }}
                                                            onCancel={() => {
                                                                setShowRecorder(false);
                                                                setInitVoiceNote(null);
                                                                setInitVoiceUrl('');
                                                            }}
                                                        />
                                                    )}

                                                    {initVoiceUrl && (
                                                        <div className="mt-2 d-flex align-items-center gap-2">
                                                            <audio src={initVoiceUrl} controls style={{ width: '100%', height: '40px' }} />
                                                            <Button size="sm" variant="outline-danger" onClick={() => { setInitVoiceNote(null); setInitVoiceUrl(''); }}>
                                                                <i className="fas fa-trash"></i>
                                                            </Button>
                                                        </div>
                                                    )}

                                                    <div className="d-flex gap-2 mt-2">
                                                        <Button size="sm" variant="primary" disabled={isUploading} onClick={() => submitInitiatedMessageHandler(order._id)}>
                                                            {isUploading ? 'Sending...' : 'Send Message'}
                                                        </Button>
                                                        <Button size="sm" variant="light" onClick={() => {
                                                            setIsInitiatingMessage(null);
                                                            setInitMessageText('');
                                                            setInitVoiceNote(null);
                                                            setInitVoiceUrl('');
                                                            setInitImage('');
                                                            setShowRecorder(false);
                                                        }}>
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Card.Footer>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>
        </div>
    );
}